import React, { useState } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import { Download, Visibility, Save, Lock } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";
import Avatar from "../../components/shared/Avatar";
import StatusBadge from "../../components/shared/StatusBadge";

// ===================== REPORTS =====================
const reportTiles = [
  {
    icon: "👥",
    name: "Employee Report",
    sub: "Headcount, tenure, turnover",
    color: "#85D1DB",
    bg: "rgba(133,209,219,0.1)",
  },
  {
    icon: "⏱️",
    name: "Attendance Report",
    sub: "Daily, weekly, monthly logs",
    color: "#6BB8C3",
    bg: "#C9FDF2",
  },
  {
    icon: "📅",
    name: "Leave Report",
    sub: "Leave balance & utilization",
    color: "#d97706",
    bg: "#fef3c7",
  },
  {
    icon: "💰",
    name: "Payroll Report",
    sub: "Salary, deductions, net pay",
    color: "#16a34a",
    bg: "#dcfce7",
  },
  {
    icon: "📈",
    name: "Performance Report",
    sub: "Scores, goals, appraisals",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.1)",
  },
  {
    icon: "🔍",
    name: "Recruitment Report",
    sub: "Applications, offers, hires",
    color: "#0d9488",
    bg: "#ccfbf1",
  },
  {
    icon: "🎓",
    name: "Training Report",
    sub: "Enrollment & completion",
    color: "#db2777",
    bg: "#fce7f3",
  },
  {
    icon: "🏢",
    name: "Department Report",
    sub: "Budget, headcount, open roles",
    color: "#9333ea",
    bg: "rgba(147,51,234,0.1)",
  },
  {
    icon: "📊",
    name: "Analytics Dashboard",
    sub: "KPIs & trends overview",
    color: "#dc2626",
    bg: "#fee2e2",
  },
];

export const Reports: React.FC = () => {
  const dispatch = useAppDispatch();
  return (
    <Box>
      <SEO title="Reports" noindex />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          Generate & Export Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() =>
            dispatch(showSnackbar({ message: "All reports exported" }))
          }
        >
          Export All
        </Button>
      </Box>
      <Grid container spacing={2}>
        {reportTiles.map((r) => (
          <Grid item xs={12} sm={6} lg={4} key={r.name}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 6px 24px rgba(0,0,0,0.1) !important",
                  transform: "translateY(-3px)",
                },
              }}
              onClick={() =>
                dispatch(showSnackbar({ message: `Generating "${r.name}"...` }))
              }
            >
              <CardContent
                sx={{
                  p: "24px !important",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: r.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 24,
                    mb: 0.5,
                  }}
                >
                  {r.icon}
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                  {r.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", flex: 1 }}>
                  {r.sub}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(showSnackbar({ message: `${r.name} exported` }));
                    }}
                    sx={{ flex: 1, fontSize: 11, borderRadius: 1.5 }}
                  >
                    Export
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(showSnackbar({ message: `Preview: ${r.name}` }));
                    }}
                    sx={{ flex: 1, fontSize: 11, borderRadius: 1.5 }}
                  >
                    Preview
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ===================== SETTINGS =====================
const permissions = [
  {
    role: "Admin",
    canView: true,
    canEdit: true,
    canApprove: true,
    canDelete: true,
  },
  {
    role: "HR Manager",
    canView: true,
    canEdit: true,
    canApprove: true,
    canDelete: false,
  },
  {
    role: "Manager",
    canView: true,
    canEdit: false,
    canApprove: true,
    canDelete: false,
  },
  {
    role: "Employee",
    canView: true,
    canEdit: false,
    canApprove: false,
    canDelete: false,
  },
  {
    role: "Finance",
    canView: true,
    canEdit: false,
    canApprove: false,
    canDelete: false,
  },
  {
    role: "Recruiter",
    canView: true,
    canEdit: false,
    canApprove: false,
    canDelete: false,
  },
];

const ROLE_COLORS: Record<string, string> = {
  Admin: "rgba(133,209,219,0.12)",
  "HR Manager": "rgba(20,184,166,0.12)",
  Manager: "rgba(182,242,209,0.12)",
  Employee: "rgba(245,158,11,0.12)",
  Finance: "rgba(34,197,94,0.12)",
  Recruiter: "rgba(244,63,94,0.12)",
};
const ROLE_TEXT: Record<string, string> = {
  Admin: "#85D1DB",
  "HR Manager": "#0d9488",
  Manager: "#6BB8C3",
  Employee: "#d97706",
  Finance: "#16a34a",
  Recruiter: "#e11d48",
};

// Using global theme for table headers

export const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const [company, setCompany] = useState({
    name: "HRCore Technologies Pvt. Ltd.",
    industry: "Information Technology",
    hq: "Ahmedabad, Gujarat, India",
    fy: "April–March",
    website: "www.hrcore.in",
    founded: "2018",
  });
  const [leave, setLeave] = useState({
    casual: 12,
    sick: 12,
    earned: 21,
    carryForward: "Up to 10 days",
    unpaid: true,
  });
  const [att, setAtt] = useState({
    startTime: "09:00",
    endTime: "18:00",
    lateAfter: 30,
    maxBreak: 60,
    minHours: 8,
  });
  const [notif, setNotif] = useState({
    emailLeave: true,
    emailPayroll: true,
    emailPerf: false,
    smsAlerts: false,
  });

  const save = (section: string) =>
    dispatch(showSnackbar({ message: `${section} saved successfully` }));

  const SectionCard: React.FC<{
    title: string;
    icon: string;
    children: React.ReactNode;
  }> = ({ title, icon, children }) => (
    <Card>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: "14px 18px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography sx={{ fontSize: 18 }}>{icon}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
      </Box>
      <CardContent
        sx={{
          p: "18px !important",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <SEO title="Settings" noindex />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Company Profile" icon="🏢">
            <TextField
              fullWidth
              size="small"
              label="Company Name"
              value={company.name}
              onChange={(e) =>
                setCompany((c) => ({ ...c, name: e.target.value }))
              }
            />
            <TextField
              fullWidth
              size="small"
              label="Industry"
              value={company.industry}
              onChange={(e) =>
                setCompany((c) => ({ ...c, industry: e.target.value }))
              }
            />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Website"
                  value={company.website}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, website: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Founded"
                  value={company.founded}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, founded: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Headquarters"
              value={company.hq}
              onChange={(e) =>
                setCompany((c) => ({ ...c, hq: e.target.value }))
              }
            />
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => save("Company profile")}
              sx={{ alignSelf: "flex-start" }}
            >
              Save Changes
            </Button>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Leave Policy" icon="📅">
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Casual Leave (days)"
                  type="number"
                  value={leave.casual}
                  onChange={(e) =>
                    setLeave((l) => ({ ...l, casual: +e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sick Leave (days)"
                  type="number"
                  value={leave.sick}
                  onChange={(e) =>
                    setLeave((l) => ({ ...l, sick: +e.target.value }))
                  }
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Earned Leave (days)"
              type="number"
              value={leave.earned}
              onChange={(e) =>
                setLeave((l) => ({ ...l, earned: +e.target.value }))
              }
            />
            <FormControl fullWidth size="small">
              <InputLabel>Carry Forward</InputLabel>
              <Select
                value={leave.carryForward}
                label="Carry Forward"
                onChange={(e) =>
                  setLeave((l) => ({ ...l, carryForward: e.target.value }))
                }
              >
                {["Up to 10 days", "Up to 15 days", "No carry forward"].map(
                  (o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={leave.unpaid}
                  onChange={(e) =>
                    setLeave((l) => ({ ...l, unpaid: e.target.checked }))
                  }
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: 13 }}>
                  Allow Unpaid Leave
                </Typography>
              }
            />
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => save("Leave policy")}
              sx={{ alignSelf: "flex-start" }}
            >
              Save Policy
            </Button>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Attendance Rules" icon="⏱️">
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Office Start"
                  type="time"
                  value={att.startTime}
                  onChange={(e) =>
                    setAtt((a) => ({ ...a, startTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Office End"
                  type="time"
                  value={att.endTime}
                  onChange={(e) =>
                    setAtt((a) => ({ ...a, endTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Late After (mins)"
                  type="number"
                  value={att.lateAfter}
                  onChange={(e) =>
                    setAtt((a) => ({ ...a, lateAfter: +e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max Break (mins)"
                  type="number"
                  value={att.maxBreak}
                  onChange={(e) =>
                    setAtt((a) => ({ ...a, maxBreak: +e.target.value }))
                  }
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Min Working Hours/Day"
              type="number"
              value={att.minHours}
              onChange={(e) =>
                setAtt((a) => ({ ...a, minHours: +e.target.value }))
              }
            />
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => save("Attendance rules")}
              sx={{ alignSelf: "flex-start" }}
            >
              Save Rules
            </Button>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Notifications" icon="🔔">
            {[
              {
                key: "emailLeave",
                label: "Email — Leave Requests",
                sub: "Notify on new leave",
              },
              {
                key: "emailPayroll",
                label: "Email — Payroll",
                sub: "Notify on payroll processing",
              },
              {
                key: "emailPerf",
                label: "Email — Performance",
                sub: "Notify on review submissions",
              },
              {
                key: "smsAlerts",
                label: "SMS Alerts",
                sub: "Critical alerts via SMS",
              },
            ].map((item) => (
              <Box key={item.key}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {item.sub}
                    </Typography>
                  </Box>
                  <Switch
                    checked={notif[item.key as keyof typeof notif] as boolean}
                    onChange={(e) =>
                      setNotif((n) => ({ ...n, [item.key]: e.target.checked }))
                    }
                    color="primary"
                  />
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => save("Notification settings")}
              sx={{ alignSelf: "flex-start", mt: 1 }}
            >
              Save Preferences
            </Button>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: 18 }}>🛡️</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                Role Permissions
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Role", "View", "Edit", "Approve", "Delete"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{ textAlign: h === "Role" ? "left" : "center" }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map((p) => (
                    <TableRow key={p.role}>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <Chip
                          label={p.role}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: "99px",
                            background: ROLE_COLORS[p.role] || "#f1f5f9",
                            color: ROLE_TEXT[p.role] || "#64748b",
                            "& .MuiChip-label": { px: 1.2 },
                          }}
                        />
                      </TableCell>
                      {["canView", "canEdit", "canApprove", "canDelete"].map(
                        (k) => (
                          <TableCell
                            key={k}
                            sx={{
                              py: 1.5,
                              px: 2,
                              borderBottom: "1px solid #f1f5f9",
                              textAlign: "center",
                            }}
                          >
                            <Switch
                              defaultChecked={p[k as keyof typeof p] as boolean}
                              color={k === "canDelete" ? "error" : "primary"}
                              size="small"
                              onChange={() =>
                                dispatch(
                                  showSnackbar({
                                    message: "Permissions updated",
                                    severity: "info",
                                  }),
                                )
                              }
                            />
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                p: "12px 18px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={() => save("Permissions")}
              >
                Save Permissions
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ===================== MY PROFILE =====================
const activities = [
  { icon: "🔐", color: "#85D1DB", label: "Logged in", time: "Today 09:02" },
  {
    icon: "✅",
    color: "#16a34a",
    label: "Leave approved — Casual (2 days)",
    time: "Apr 8, 2026",
  },
  {
    icon: "📈",
    color: "#6BB8C3",
    label: "Q1 performance review submitted",
    time: "Apr 5, 2026",
  },
  {
    icon: "💰",
    color: "#d97706",
    label: "March payslip generated",
    time: "Apr 1, 2026",
  },
  {
    icon: "🎓",
    color: "#7c3aed",
    label: "Enrolled in React Development",
    time: "Mar 28, 2026",
  },
];

export const MyProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const employees = useAppSelector((s) => s.data.employees);
  const employee =
    employees.find((e) => e.email === user?.email) || employees[0];
  const [tab, setTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [phone, setPhone] = useState("+91 98765 43210");
  const [address, setAddress] = useState("Ahmedabad, Gujarat, India");
  const [emergency, setEmergency] = useState("Raj Sharma · +91 99887 76655");
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  if (!user) return null;

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <SEO title="My Profile" noindex />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4} lg={3}>
          <Card sx={{ mb: 2 }}>
            <Box
              sx={{
                height: 80,
                background: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: -5,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  border: "3px solid #fff",
                  borderRadius: "50%",
                  display: "inline-flex",
                }}
              >
                <Avatar
                  initials={user.initials || "??"}
                  color={user.avColor}
                  size={80}
                />
              </Box>
            </Box>
            <CardContent
              sx={{ textAlign: "center", pt: 0, pb: "16px !important" }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                {user.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#85D1DB",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.5,
                }}
              >
                {user.role === "hr"
                  ? "HR Manager"
                  : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.4 }}>
                🏢 {user.dept}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#94a3b8",
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                #{user.empId}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  gap: 1,
                }}
              >
                {[
                  { v: "4.2", l: "Avg Rating" },
                  { v: "14", l: "Leave Left" },
                  { v: "91%", l: "Attendance" },
                ].map((s) => (
                  <Box key={s.l}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {s.v}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {s.l}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <Box sx={{ p: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                ⏱️ Recent Activity
              </Typography>
            </Box>
            <CardContent
              sx={{
                p: "12px 16px !important",
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
              }}
            >
              {activities.map((a, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      background: a.color + "20",
                      color: a.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    {a.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}
                    >
                      {a.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10, color: "#94a3b8", mt: 0.2 }}
                    >
                      {a.time}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Card>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: "1px solid #e5e7eb", px: 1 }}
              TabIndicatorProps={{ style: { background: "#85D1DB" } }}
            >
              {["Personal Info", "Security"].map((t, i) => (
                <Tab
                  key={i}
                  label={t}
                  sx={{
                    fontSize: 13,
                    color: tab === i ? "#85D1DB" : "#64748b",
                  }}
                />
              ))}
            </Tabs>
            <CardContent sx={{ p: "20px !important" }}>
              {tab === 0 && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      Personal Information
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditMode(!editMode)}
                      sx={{ fontSize: 12 }}
                    >
                      {editMode ? "Cancel" : "✏️ Edit"}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      border: "1px solid #e5e7eb",
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {[
                      ["Full Name", user.name],
                      ["Email", user.email],
                      ["Employee ID", user.empId],
                      ["Department", user.dept],
                      employee && ["Date of Joining", employee.joined],
                      employee && [
                        "Status",
                        <StatusBadge status={employee.status} />,
                      ],
                    ]
                      .filter(Boolean)
                      .map((item: any, i) => (
                        <Box
                          key={i}
                          sx={{
                            p: "12px 16px",
                            borderBottom: "1px solid #f1f5f9",
                            borderRight:
                              i % 2 === 0 ? "1px solid #f1f5f9" : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              mb: 0.5,
                            }}
                          >
                            {item[0]}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily:
                                item[0] === "Employee ID"
                                  ? "'JetBrains Mono',monospace"
                                  : undefined,
                            }}
                          >
                            {item[1]}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                    Contact Details
                  </Typography>
                  {editMode ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Emergency Contact"
                        value={emergency}
                        onChange={(e) => setEmergency(e.target.value)}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={<Save />}
                          onClick={() => {
                            setEditMode(false);
                            dispatch(
                              showSnackbar({ message: "Profile updated" }),
                            );
                          }}
                        >
                          Save Profile
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      {[
                        ["Phone", phone],
                        ["Emergency Contact", emergency],
                        ["Address", address],
                      ].map(([k, v], i) => (
                        <Box
                          key={k}
                          sx={{
                            p: "12px 16px",
                            borderBottom: "1px solid #f1f5f9",
                            borderRight:
                              i % 2 === 0 && k !== "Address"
                                ? "1px solid #f1f5f9"
                                : "none",
                            gridColumn: k === "Address" ? "1/-1" : "auto",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              mb: 0.5,
                            }}
                          >
                            {k}
                          </Typography>
                          <Typography sx={{ fontSize: 13 }}>{v}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {tab === 1 && (
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                    Change Password
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mb: 3,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Current Password"
                      type="password"
                      value={oldPass}
                      onChange={(e) => setOldPass(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="New Password"
                      type="password"
                      value={newPass}
                      onChange={(e) => setNewPass(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Confirm New Password"
                      type="password"
                      value={confirmPass}
                      onChange={(e) => setConfirmPass(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      startIcon={<Lock />}
                      onClick={() => {
                        if (newPass !== confirmPass) {
                          dispatch(
                            showSnackbar({
                              message: "Passwords do not match",
                              severity: "error",
                            }),
                          );
                          return;
                        }
                        setOldPass("");
                        setNewPass("");
                        setConfirmPass("");
                        dispatch(
                          showSnackbar({
                            message: "Password changed successfully",
                          }),
                        );
                      }}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Update Password
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                    Active Sessions
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      background: "#f8fafc",
                      borderRadius: 2,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Typography sx={{ fontSize: 22 }}>💻</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        Chrome · Windows 11 · Ahmedabad, IN
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "#16a34a", mt: 0.2 }}
                      >
                        Active now · Current session
                      </Typography>
                    </Box>
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: "99px",
                        background: "#dcfce7",
                        color: "#16a34a",
                        "& .MuiChip-label": { px: 1.2 },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
